import * as React from "react";

import { GridDisplay } from "@impero-com/common/components/form/legacyFields/gridDisplay";
import { assertNever } from "@impero-com/common/utils/obj";
import { nullToEmpty } from "@impero-com/common/utils/string";
import { Radio } from "antd";

import {
    FormComponentProps,
    ValidationRule,
    casterWithIdWrapper,
    EditFormCreate,
    validationRulesConverter,
    CustomizedValidation,
} from "utils/form";

import { BaseFormDisplay } from "./formDisplay";
import * as theme from "./theme.css";

import { BaseFormField, EditInput, EditProps } from ".";

/**
 * The definition of an Option on a Radio element
 * @field value the "real", "inner" value of the Radio field once selected (to be sent in a JSON probably)
 * @field label the label that the Radio will show for the user to select
 * @field disabled should the Radio be greyed out
 */
interface RadioInputOption {
    value: string;
    label: string;
    disabled?: boolean;
}

interface CreateRadioInputProps<
    ID extends string,
    R,
> extends FormComponentProps<ID, string | undefined, R> {
    id: ID;
    initialValue: string | undefined;
    label: string;
    validators?: ValidationRule<string | undefined>[];
    options: RadioInputOption[];
    columns?: number;
    className?: string;
    validationError?: CustomizedValidation;
}

class CreateRadioInput<ID extends string, R> extends React.PureComponent<
    CreateRadioInputProps<ID, R>
> {
    render() {
        const { label, validationError } = this.props;

        return (
            <BaseFormField
                getComponent={() => this.getComponent(this.props.options)}
                hasFeedback={false}
                label={label}
                validationError={validationError}
            />
        );
    }

    deselect = (clickedValue: string | undefined) => {
        const {
            form: { getFieldValue },
            id,
        } = this.props;
        if (getFieldValue(id) === clickedValue) {
            const { setFieldsValue } = this.props.form;
            setFieldsValue(
                casterWithIdWrapper<string | undefined>()(
                    this.props.id,
                    this.props.form,
                    undefined
                )
            );
        }
    };

    getComponent = (options: RadioInputOption[]) => {
        const { form, id, validators, label, initialValue, className } =
            this.props;
        const { getFieldDecorator } = form;
        return getFieldDecorator(id, {
            rules: validationRulesConverter<string | undefined>()(
                id,
                form,
                validators,
                label
            ),
            valuePropName: "value",
            trigger: "onChange",
            validateFirst: true,
            preserve: true,
            initialValue,
        })(
            <Radio.Group
                className={`${theme.imperoGrid} ${nullToEmpty(className)}`}
            >
                <GridDisplay
                    columns={this.props.columns ? this.props.columns : 1}
                    items={options}
                    renderItem={(opt) => renderRadioButton(opt, this.deselect)}
                />
            </Radio.Group>
        );
    };
}

type EditRadioInputProps = EditProps<string | undefined>;

const EditRadioInputForm = EditFormCreate<
    string | undefined,
    EditRadioInputProps
>()(CreateRadioInput);

const EditRadioInput = EditInput<string | undefined, EditRadioInputProps>(
    EditRadioInputForm
);

interface RadioInputCommonProps {
    label: string;
    options: RadioInputOption[];
    columns?: number;
    className?: string;
}

class ReadOnlyRadioInput extends React.PureComponent<
    RadioInputCommonProps & { value?: string }
> {
    render = () => {
        const { label, value, className } = this.props;
        return (
            <BaseFormDisplay label={label}>
                <Radio.Group
                    disabled
                    value={value}
                    className={`${theme.imperoGrid} ${nullToEmpty(className)}`}
                >
                    <GridDisplay
                        columns={this.props.columns ? this.props.columns : 1}
                        items={this.props.options}
                        renderItem={(option) =>
                            renderRadioButton(option, undefined)
                        }
                    />
                </Radio.Group>
            </BaseFormDisplay>
        );
    };
}

/**
 * Renders a RadioInputOption as a Radio. For use in GridDisplay.
 */
function renderRadioButton(
    opt: RadioInputOption,
    callbackDeselect?: (msg?: string) => void | undefined
): React.ReactNode {
    return (
        <Radio
            key={opt.value}
            value={opt.value}
            disabled={opt.disabled}
            onClick={() => {
                if (callbackDeselect !== undefined) {
                    callbackDeselect(opt.value);
                }
            }}
        >
            {opt.label}
        </Radio>
    );
}

type RadioInputProps<ID extends string, R> =
    | ({ type: "READ_ONLY" } & RadioInputCommonProps & { value?: string })
    | ({ type: "EDIT" } & EditRadioInputProps & RadioInputCommonProps)
    | ({ type: "CREATE" } & CreateRadioInputProps<ID, R> & {
              columns?: number;
          });

class RadioInput<ID extends string, R> extends React.PureComponent<
    RadioInputProps<ID, R>
> {
    render = () => {
        switch (this.props.type) {
            case "READ_ONLY": {
                const { type: _type, ...rest } = this.props;
                return <ReadOnlyRadioInput {...rest} />;
            }
            case "EDIT": {
                const { type: _type, ...rest } = this.props;
                return <EditRadioInput {...rest} />;
            }
            case "CREATE": {
                const { type: _type, ...rest } = this.props;
                return <CreateRadioInput {...rest} />;
            }
            default:
                return assertNever(this.props);
        }
    };
}
export type {
    RadioInputProps,
    RadioInputOption,
    RadioInputCommonProps,
    EditRadioInputProps,
    CreateRadioInputProps,
};
export { RadioInput };
